import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileText,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface AnalyticsMetricsProps {
  data: {
    totalBudgets: number;
    totalValue: number;
    conversionRate: number;
    averageTicket: number;
    growth: {
      budgets: number;
      value: number;
      conversion: number;
    };
  };
  loading?: boolean;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  growth?: number;
  format?: 'currency' | 'percentage' | 'number';
  loading?: boolean;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

function MetricCard({
  title,
  value,
  icon: Icon,
  growth,
  format = 'number',
  loading = false,
  variant = 'default',
}: MetricCardProps) {
  const formatValue = (val: string | number) => {
    if (loading) return val;

    switch (format) {
      case 'currency':
        return formatCurrency(Number(val));
      case 'percentage':
        return `${Number(val).toFixed(1)}%`;
      default:
        return val.toString();
    }
  };

  const getGrowthColor = (growthValue: number) => {
    if (growthValue > 0) return 'text-finance-green';
    if (growthValue < 0) return 'text-finance-red';
    return 'text-foreground';
  };

  const getGrowthIcon = (growthValue: number) => {
    if (growthValue > 0) return ArrowUpRight;
    if (growthValue < 0) return ArrowDownRight;
    return TrendingUp;
  };

  const variantStyles = {
    default: 'border-border',
    success: 'border-green-500/20 bg-green-500/10',
    warning: 'border-amber-500/20 bg-amber-500/10',
    danger: 'border-red-500/20 bg-red-500/10',
  };

  if (loading) {
    return (
      <Card className={cn('card-elevated', variantStyles[variant])}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-3 w-20" />
        </CardContent>
      </Card>
    );
  }

  const GrowthIcon = growth !== undefined ? getGrowthIcon(growth) : TrendingUp;
  const growthColor = growth !== undefined ? getGrowthColor(growth) : 'text-foreground';

  return (
    <Card className={cn('card-elevated', variantStyles[variant])}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{formatValue(value)}</div>
        {growth !== undefined && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
            <GrowthIcon className={cn('h-3 w-3', growthColor)} />
            <span className={growthColor}>
              {growth > 0 ? '+' : ''}{growth.toFixed(1)}%
            </span>
            <span>vs período anterior</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AnalyticsMetrics({ data, loading = false }: AnalyticsMetricsProps) {
  const getConversionVariant = (rate: number): MetricCardProps['variant'] => {
    if (rate >= 70) return 'success';
    if (rate >= 50) return 'warning';
    return 'default';
  };

  return (
    <div className="space-y-6">
      {/* Primary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total de Orçamentos"
          value={data.totalBudgets}
          icon={FileText}
          growth={data.growth.budgets}
          loading={loading}
        />

        <MetricCard
          title="Valor Total"
          value={data.totalValue}
          icon={DollarSign}
          growth={data.growth.value}
          format="currency"
          loading={loading}
        />

        <MetricCard
          title="Taxa de Conversão"
          value={data.conversionRate}
          icon={Target}
          growth={data.growth.conversion}
          format="percentage"
          variant={getConversionVariant(data.conversionRate)}
          loading={loading}
        />

        <MetricCard
          title="Ticket Médio"
          value={data.averageTicket}
          icon={BarChart3}
          format="currency"
          loading={loading}
        />
      </div>

      {/* Performance Indicators */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="card-elevated">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Target className="h-4 w-4" />
                Desempenho de Conversão
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-finance-green">
                    {data.conversionRate.toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground">Taxa de aprovação</div>
                </div>
                <Badge
                  variant={data.conversionRate >= 70 ? 'default' : 'secondary'}
                  className={cn(
                    data.conversionRate >= 70
                      ? 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20'
                      : 'bg-muted text-muted-foreground border-border'
                  )}
                >
                  {data.conversionRate >= 70 ? 'Excelente' : 'Regular'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Crescimento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Orçamentos</span>
                  <span className={cn(
                    data.growth.budgets >= 0 ? 'text-finance-green' : 'text-finance-red'
                  )}>
                    {data.growth.budgets >= 0 ? '+' : ''}{data.growth.budgets.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Valor</span>
                  <span className={cn(
                    data.growth.value >= 0 ? 'text-finance-green' : 'text-finance-red'
                  )}>
                    {data.growth.value >= 0 ? '+' : ''}{data.growth.value.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Conversão</span>
                  <span className={cn(
                    data.growth.conversion >= 0 ? 'text-finance-green' : 'text-finance-red'
                  )}>
                    {data.growth.conversion >= 0 ? '+' : ''}{data.growth.conversion.toFixed(1)}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Métricas de Valor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Valor Total</span>
                  <span className="font-medium">{formatCurrency(data.totalValue)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Ticket Médio</span>
                  <span className="font-medium">{formatCurrency(data.averageTicket)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Valor/Orçamento</span>
                  <span className="font-medium">
                    {data.totalBudgets > 0
                      ? formatCurrency(data.totalValue / data.totalBudgets)
                      : formatCurrency(0)
                    }
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}